import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface CollectionGame {
  id: string;
  gameId: string;
  status: 'backlog' | 'wishlist' | 'completed' | 'playing';
  platform?: string;
  dateAdded: string;
  game: {
    id: string;
    title: string;
    imageUrl?: string;
    developer?: string;
    publisher?: string;
  };
}

interface CollectionState {
  games: CollectionGame[];
  total: number;
  hasMore: boolean;
  page: number;
  statusFilter: 'backlog' | 'wishlist' | 'completed' | 'playing' | 'all';
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
}

const initialState: CollectionState = {
  games: [],
  total: 0,
  hasMore: true,
  page: 0,
  statusFilter: 'all',
  isLoading: false,
  isLoadingMore: false,
  error: null,
};

const collectionSlice = createSlice({
  name: 'collection',
  initialState,
  reducers: {
    setGames: (
      state,
      action: PayloadAction<{
        games: CollectionGame[];
        total: number;
        hasMore: boolean;
      }>,
    ) => {
      state.games = action.payload.games;
      state.total = action.payload.total;
      state.hasMore = action.payload.hasMore;
    },
    appendGames: (
      state,
      action: PayloadAction<{ games: CollectionGame[]; hasMore: boolean }>,
    ) => {
      state.games.push(...action.payload.games);
      state.hasMore = action.payload.hasMore;
    },
    updateGameStatus: (
      state,
      action: PayloadAction<{
        gameId: string;
        status: CollectionGame['status'];
      }>,
    ) => {
      const game = state.games.find(g => g.gameId === action.payload.gameId);
      if (game) {
        game.status = action.payload.status;
      }
    },
    addGameToCollection: (state, action: PayloadAction<CollectionGame>) => {
      state.games.unshift(action.payload);
      state.total += 1;
    },
    removeGameFromCollection: (state, action: PayloadAction<string>) => {
      state.games = state.games.filter(g => g.gameId !== action.payload);
      state.total -= 1;
    },
    setStatusFilter: (
      state,
      action: PayloadAction<CollectionState['statusFilter']>,
    ) => {
      state.statusFilter = action.payload;
      state.page = 0;
      state.games = [];
      state.hasMore = true;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMore = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearError: state => {
      state.error = null;
    },
  },
});

export const {
  setGames,
  appendGames,
  updateGameStatus,
  addGameToCollection,
  removeGameFromCollection,
  setStatusFilter,
  setPage,
  setLoadingMore,
  setLoading,
  setError,
  clearError,
} = collectionSlice.actions;

export default collectionSlice.reducer;
